import './TimePicker.scss'
import React from 'react'
import moment from 'moment-timezone'
import PropTypes from 'prop-types'
import clsx from 'clsx'

import { OrganizationContext } from '../../Contexts/OrganizationProvider'
import { DATE_TODAY, getTimesInDay } from 'misc/calendar'
import Picker from 'components/DateTimePicker/Picker'

const ClockIcon = () => {
  return (
    <svg width="20px" height="20px" viewBox="0 0 20 20" className="ClockIcon">
      <path d="M19.2016993,6.10173203 C19.7328758,7.3395098 20,8.62771242 20,9.98352941 C20,11.3396078 19.7326471,12.6387582 19.201732,13.8765686 C18.1470588,16.3346732 16.3552288,18.1337255 13.8763725,19.1909804 C12.6376144,19.7191176 11.3394118,19.9890196 9.98356209,19.9890196 C8.62771242,19.9890196 7.33954248,19.7219281 6.10173203,19.1909804 C3.64362745,18.1363072 1.84362745,16.3388562 0.787320261,13.8765686 C0.258006536,12.6427124 0,11.3396078 0,9.98352941 C0,8.62771242 0.258006536,7.3353268 0.787320261,6.10173203 C1.84362745,3.63944444 3.64339869,1.85271242 6.10173203,0.798300654 C7.33931373,0.267352941 8.6277451,0 9.98356209,0 C11.3396078,0 12.6376144,0.269901961 13.8765686,0.798300654 C16.3554248,1.85552288 18.1472549,3.64339869 19.2016993,6.10173203 Z M18.0530035,6.59468013 C17.1244438,4.42983476 15.5673467,2.87835036 13.3861944,1.94809764 C12.2953934,1.48288448 11.1636733,1.25 9.98356209,1.25 C8.8018032,1.25 7.68069525,1.48107395 6.59446289,1.94708971 C4.42404434,2.878011 2.86304852,4.43373644 1.93603895,6.59462692 C1.47703363,7.66436408 1.25,8.78814045 1.25,9.98352941 C1.25,11.1821817 1.47816332,12.3163432 1.93607562,13.3837592 C2.86522041,15.549627 4.42940759,17.1132537 6.59448985,18.0422029 C7.68064408,18.508099 8.80150123,18.7390196 9.98356209,18.7390196 C11.1634605,18.7390196 12.2954298,18.5061409 13.3859774,18.0411923 C15.5616081,17.1132659 17.1219069,15.5537838 18.0529435,13.3838364 C18.517745,12.3001675 18.75,11.1685157 18.75,9.98352941 C18.75,8.80144289 18.5190246,7.68062919 18.0530035,6.59468013 Z M10.610719,10.0266993 L13.6725817,13.1104575 C13.9074837,13.3470261 13.9240196,13.7118954 13.6725817,13.963366 C13.4539542,14.1821569 13.0494444,14.1821569 12.8306536,13.963366 L9.56094771,10.6938889 C9.4298366,10.562549 9.375,10.3985294 9.3969281,10.2016667 C9.3969281,10.1689869 9.375,10.1251307 9.375,10.1141503 L9.375,4.36235294 C9.375,4.02339869 9.65937908,3.75 9.97640523,3.75 C10.3153922,3.75 10.610719,4.02339869 10.610719,4.36235294 L10.610719,10.0266993 Z" />
    </svg>
  )
}

class SelectableTime extends React.Component {
  constructor(props) {
    super(props)
    this.selectedItem = React.createRef()
    this.scrollableContainer = React.createRef()
  }

  componentDidMount() {
    document.addEventListener('click', this.handleClick)

    const selectedItem = this.selectedItem.current
    const scrollableContainer = this.scrollableContainer.current

    if (selectedItem && scrollableContainer) {
      scrollableContainer.scrollTop =
        selectedItem.offsetTop -
        scrollableContainer.clientHeight / 2 +
        selectedItem.clientHeight / 2
    }
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.handleClick)
  }

  handleClick = e => {
    if (
      this.props.timePickerContainer &&
      this.props.timePickerContainer.current &&
      this.props.timePickerContainer.current.contains(e.target) === false
    ) {
      this.props.handleCloseTimePicker()
    }
  }

  handleSelect = dateTime => event =>
    this.props.handleSelect(moment.parseZone(dateTime).format())

  render() {
    const { activeDate, displayFormat } = this.props

    const timeSlots = getTimesInDay(activeDate)
    const activeTime = moment.parseZone(activeDate).format('HH:mm')

    return (
      <div className="SelectableTime" ref={this.scrollableContainer}>
        {timeSlots.map((time, i) => {
          const formattedTime = moment.parseZone(time).format('HH:mm')
          const formattedTime12Hour = moment.parseZone(time).format('h:mm A')
          const isSelected = activeTime === formattedTime

          return (
            <button
              className="time"
              key={i}
              ref={isSelected ? this.selectedItem : null}
              onClick={this.handleSelect(time)}
              data-active={isSelected}
              disabled={time.isBefore(DATE_TODAY)}>
              {displayFormat === '24h'
                ? formattedTime
                : displayFormat === '12h'
                ? formattedTime12Hour
                : formattedTime}
            </button>
          )
        })}
      </div>
    )
  }
}

export class TimePicker extends React.Component {
  constructor(props) {
    super(props)
    this.timePickerContainer = React.createRef()
  }

  handleKeyDown = e => {
    e.keyCode === 27 && this.props.handleCloseTimePicker()
  }

  handleSelect = time => {
    this.props.handleClickTime(time)
    this.props.handleCloseTimePicker()
  }

  handlePickerClick = () => {
    if (this.props.readOnly) {
      return
    }

    this.props.handleTimePickerClick()
  }

  render() {
    const {
      activeDate,
      timesIsOpen,
      readOnly,
      errored,
      handleCloseTimePicker
    } = this.props

    const activeTime = moment.parseZone(activeDate).format('HH:mm')
    const activeTime12Hour = moment.parseZone(activeDate).format('h:mm A')

    return (
      <OrganizationContext.Consumer>
        {({ settings }) => {
          const displayFormat =
            settings && settings.time_preferences.display_format

          return (
            <div
              className={clsx('TimePicker', {
                'is-readonly': readOnly,
                'is-errored': errored
              })}
              ref={this.timePickerContainer}
              onKeyDown={this.handleKeyDown}
              tabIndex="-1">
              <Picker
                icon={<ClockIcon />}
                value={
                  displayFormat === '24h'
                    ? activeTime
                    : displayFormat === '12h'
                    ? activeTime12Hour
                    : activeTime
                }
                handleClick={this.handlePickerClick}
              />
              {timesIsOpen && !readOnly && (
                <SelectableTime
                  activeDate={activeDate}
                  handleSelect={this.handleSelect}
                  timePickerContainer={this.timePickerContainer}
                  handleCloseTimePicker={handleCloseTimePicker}
                  displayFormat={displayFormat}
                />
              )}
            </div>
          )
        }}
      </OrganizationContext.Consumer>
    )
  }
}

export default TimePicker

TimePicker.propTypes = {
  activeDate: PropTypes.object,
  timesIsOpen: PropTypes.bool.isRequired,
  readOnly: PropTypes.bool,
  handleTimePickerClick: PropTypes.func.isRequired,
  handleCloseTimePicker: PropTypes.func.isRequired,
  handleClickTime: PropTypes.func.isRequired
}

SelectableTime.propTypes = {
  activeDate: PropTypes.object,
  handleSelect: PropTypes.func.isRequired
}
